import { OverlayViewF, OVERLAY_MOUSE_TARGET } from '@react-google-maps/api';
import {
  getMapGoalCount,
  getIsGlobalBySpotId,
  getRallyIdsBySpotId,
  getStampCampaign,
  RALLY_META,
} from '../../constants/mapConfig';
import { getMapStampStackImagePath } from '../../utils/stampUtils';

// ── ピン色定義 ────────────────────────────────────────────────────────────
// 全国版: RALLY_META.isGlobal && SPOT_CONFIG.isGlobal が両方 true
const PIN_COLOR_GLOBAL     = '#920000';
// 単独版: 上記以外
const PIN_COLOR_INDIVIDUAL = '#EA5521';
// 達成済み枠線色
const ACHIEVED_BORDER_COLOR = '#84cc16'; // lime-400

/**
 * SpotMarker - スポットアイコン
 *
 * ■ mapMode='overview' (zoom < 9):
 *   - isGlobal: true のスポットのみ表示（単独ラリーは非表示）
 *   - ピン: /images/common/pin-default.png（エリア問わず共通）
 *
 * ■ mapMode='detail' (9 ≤ zoom < 12):
 *   - isGlobal: true のスポットのみ（NationalMapPage 側でフィルター済み）
 *   - ピン円色: isGlobal: true → #920000（全国版カラーを優先）
 *
 * ■ mapMode='expanded' (zoom ≥ 12):
 *   - 全スポット表示
 *   - ピン円色: 周辺ラリー（単独版）優先（現状通り）
 *       全ラリーが isGlobal: true → #920000
 *       1つでも非 isGlobal        → #EA5521
 */
const SpotMarker = ({ spot, onTap, mapMode = 'detail' }) => {
  if (!spot?.lat || !spot?.lng) return null;

  // SPOT_CONFIG.isGlobal: overview の表示フィルター & detail のピン色判定に共用
  const spotIsGlobal = getIsGlobalBySpotId(spot.id);

  // ── overview: 全国版スポットのみ・pin-default.png ────────────────
  if (mapMode === 'overview') {
    // 単独ラリーのスポットは縮小表示しない
    if (!spotIsGlobal) return null;

    return (
      <OverlayViewF
        position={{ lat: spot.lat, lng: spot.lng }}
        mapPaneName={OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={() => ({ x: -15, y: -40 })}
      >
        <div
          className="relative cursor-pointer transition-transform hover:scale-125"
          style={{ width: 30, height: 40 }}
          onClick={() => onTap(spot.id)}
          role="button"
          tabIndex={0}
          aria-label={spot.name}
          onKeyDown={(e) => e.key === 'Enter' && onTap(spot.id)}
        >
          <img
            src="/images/common/pin-default.png"
            alt={spot.name}
            style={{ width: 30, height: 40 }}
            draggable={false}
          />
        </div>
      </OverlayViewF>
    );
  }

  // ── detail / expanded: 通常ピン表示 ─────────────────────────────
  // detail  : isGlobal: true → #920000（全国版カラー優先）
  // expanded: 周辺ラリーを優先（現状通り）
  //           全ラリーが isGlobal: true → #920000
  //           1つでも非 isGlobal        → #EA5521
  const rallyIds = getRallyIdsBySpotId(spot.id);
  let pinColor;
  if (mapMode === 'detail') {
    pinColor = spotIsGlobal ? PIN_COLOR_GLOBAL : PIN_COLOR_INDIVIDUAL;
  } else {
    const hasNonGlobalRally = rallyIds.some((rId) => !RALLY_META[rId]?.isGlobal);
    const allRalliesGlobal = rallyIds.length > 0 && !hasNonGlobalRally;
    pinColor = allRalliesGlobal ? PIN_COLOR_GLOBAL : PIN_COLOR_INDIVIDUAL;
  }

  const configGoalCount = getMapGoalCount(spot.id);
  const goalCount  = configGoalCount > 0 ? configGoalCount : 250;
  const stampCount = spot.stampCount ?? 0;
  const isAchieved = stampCount >= goalCount;
  const stampCampaign = getStampCampaign(spot.id);

  // スタンプ積み上げ画像パス
  const stampImagePath = getMapStampStackImagePath(stampCount);

  return (
    <OverlayViewF
      position={{ lat: spot.lat, lng: spot.lng }}
      mapPaneName={OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(width, height) => ({
        x: -(width / 2),
        y: -height,
      })}
    >
      <div
        className="flex cursor-pointer flex-col items-center"
        onClick={() => onTap(spot.id)}
        role="button"
        tabIndex={0}
        aria-label={`${spot.name} スタンプ${stampCount}個`}
        onKeyDown={(e) => e.key === 'Enter' && onTap(spot.id)}
      >
        {/* スタンプ積み上げ画像（0スタンプ時は非表示） */}
        {stampImagePath && (
          <img
            src={stampImagePath}
            alt={`スタンプ ${stampCount}個`}
            className="h-14 w-14 object-contain drop-shadow-sm"
            draggable={false}
          />
        )}

        {/* ピン円 */}
        {isAchieved ? (
          /* 達成済み: 種別カラー + lime 枠線 */
          <div
            className="mt-0.5 flex h-9 w-9 flex-col items-center justify-center rounded-full shadow-md"
            style={{
              backgroundColor: pinColor,
              border: `3px solid ${ACHIEVED_BORDER_COLOR}`,
            }}
          >
            <span className="text-xs font-bold leading-none text-white">{stampCount}</span>
            <span className="text-[9px] leading-none text-white/70">/{goalCount}</span>
          </div>
        ) : (
          /* 未達成: 種別カラー + 白枠 */
          <div
            className="mt-0.5 flex h-9 w-9 flex-col items-center justify-center rounded-full border-2 border-white shadow-md"
            style={{ backgroundColor: pinColor }}
          >
            <span className="text-xs font-bold leading-none text-white">{stampCount}</span>
            <span className="text-[9px] leading-none text-white/70">/{goalCount}</span>
          </div>
        )}

        {/* スポット名ラベル（+ 達成バッジ） */}
        <div className="mt-0.5 flex items-center gap-1">
          {isAchieved && (
            <span
              className="rounded px-1 text-[9px] font-bold text-white"
              style={{ backgroundColor: ACHIEVED_BORDER_COLOR }}
            >
              達成
            </span>
          )}
          <span className="max-w-[80px] truncate rounded bg-white/90 px-1 text-[10px] font-medium text-gray-700 shadow-sm">
            {spot.name}
          </span>
        </div>

        {/* スタンプ倍数キャンペーンラベル */}
        {stampCampaign && (
          <div
            className="mt-0.5 flex flex-col items-center rounded-md px-1.5 py-0.5 text-center leading-tight shadow-md"
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
              border: '1.5px solid #fff',
            }}
          >
            <span className="text-[9px] font-black text-white tracking-wide">スタンプ{stampCampaign.multiplier}倍</span>
            <span className="text-[9px] font-black text-white tracking-wide">キャンペーン！</span>
          </div>
        )}
      </div>
    </OverlayViewF>
  );
};

export default SpotMarker;
